import React, { useState, type FormEvent, useEffect } from "react";
import { auth } from "../../../config/firebase";
import { useCause } from "../../../contexts/CauseContext";
import { useTeam } from "../../../contexts/TeamContext";
import styles from "./AddTeam.module.css";
import { dayOptions } from "../config/dayOptions";
import { MultiSelect } from "../components/MultiSelect";
import { Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useMoodsTranslator } from "../../settings/components/useMoodsTranslator";
import { useLocation } from "react-router-dom";
import { FeelingCause } from "../../../types/feeling-cause";
import { TeamNameInput } from "../components/TeamNameInput";
import { TeamService } from "../../../services/TeamService";
import axios from "axios";

export type AddTeamModalProps = {
  onClose?: () => void;
};

const normalizeTeamName = (value: string) => value.trim().toLowerCase();

const DEFAULT_NOTIFICATION_DAYS = ["Tuesday", "Thursday"];

const AddTeam = ({ onClose }: AddTeamModalProps) => {
  const { setActiveCauseTypes, activeCauseTypes, defaultCauseTypes } =
    useCause();
  const {
    organizationId,
    setTeams,
    teams,
    activeTeamCauseTypes,
    setActiveTeamCauseTypes,
    teamId,
  } = useTeam();

  const [notifications, setNotifications] = useState<string[]>(
    DEFAULT_NOTIFICATION_DAYS
  );
  const [name, setName] = useState("");
  const [savedTeamCauses, setSavedTeamCauses] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Enter a team name");
      return;
    }
    const uid = auth.currentUser?.uid;
    if (!uid) {
      setError("No user found");
      return;
    }
    const key = normalizeTeamName(trimmed);
    const list = Array.isArray(teams) ? teams : [];
    if (
      key &&
      list.some(
        (t: { name?: string }) => normalizeTeamName(t?.name ?? "") === key
      )
    ) {
      setError("A team with this name already exists");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const causeTypeIds = Array.isArray(savedTeamCauses)
        ? savedTeamCauses.filter(Boolean)
        : [];

      const newTeam = await TeamService.addTeam({
        name: trimmed,
        organizationId,
        leadUid: uid,
        notifications,
        causeTypeIds,
      });


      axios
        .post(
          process.env.BASE_URL +
          "/team",
          {
            name: trimmed,
            organizationId: organizationId,
            notifications: "yes"
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
        .then((response) => {
          console.log("Created team ID:", response.data);
        })
        .catch((error) => {
          console.error("Error creating team:", error);
        });


      setTeams((prev: unknown) => {
        const list = Array.isArray(prev) ? prev : [];
        if (list.some((t: { id?: string }) => t?.id === newTeam.id))
          return list;
        return [...list, newTeam];
      });

      setName("");
      setNotifications(DEFAULT_NOTIFICATION_DAYS);
      setSavedTeamCauses([]);
      onClose?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add team");
    } finally {
      setSubmitting(false);
    }
  };
  const { t } = useTranslation();
  const moodsTranslator = useMoodsTranslator();
  const { state } = useLocation();
  const type = state?.type;

  useEffect(() => {
    const merged = new Map();
    (defaultCauseTypes || []).forEach((cause: FeelingCause) =>
      merged.set(cause.id, cause)
    );
    (activeCauseTypes || []).forEach((cause: FeelingCause) =>
      merged.set(cause.id, cause)
    );
  }, [activeTeamCauseTypes, activeCauseTypes, defaultCauseTypes, type, teamId]);

  const visibleAvailableOptions = dayOptions.map((option) =>
    option.link === "/app/settings/teams"
      ? {
          ...option,
          state: { ...option.state },
        }
      : option
  );

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div className="settings-element-header">
          <button
            type="button"
            onClick={() => onClose?.()}
            disabled={submitting}
            className={styles["submit-button"]}
            style={{
              cursor: submitting ? "not-allowed" : "pointer",
            }}
            aria-label="Cancel add team"
          >
            <X className={styles["cancel-button"]} />
          </button>
          <p className={"settings-element-title page-title"}>Teams</p>

          <div></div>
          <button
            type="submit"
            disabled={submitting || !organizationId || !name.trim()}
            className={styles["submit-button"]}
            style={{
              cursor:
                submitting || !organizationId || !name.trim()
                  ? "not-allowed"
                  : "pointer",
            }}
            aria-label={submitting ? "Saving team" : "Add team"}
          >
            <Check className={styles["cancel-button"]} />
          </button>
        </div>

        <div>
          {error ? <p>{error}</p> : null}
          <TeamNameInput name={name} setName={setName} />
          <span className="secondary-title">Check-In days</span>
          <MultiSelect
            options={visibleAvailableOptions}
            initialSelectedValues={notifications}
            onSelectionChange={setNotifications}
          />
          hello


          {/*
          <span className="secondary-title">Causes</span>
          <MultiSelect
            moodsTranslator={moodsTranslator}
            causeTypes={defaultCauseTypes}
            organizationId={organizationId}
            t={t}
            type={type}
            setActiveCauseTypes={setActiveCauseTypes}
            setActiveTeamCauseTypes={setActiveTeamCauseTypes}
            initialSelectedValues={savedTeamCauses}
            onSelectionChange={setSavedTeamCauses}
          />
          */}
        </div>
      </form>
    </div>
  );
};

export default AddTeam;
